import React, { useState, useEffect } from "react";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  List,
  ListItem,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../../../theme/panelStyles";
import { webshareApi } from "../../../../../services/api/multi/webshare_credentials";
const WebshareCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingCredential, setEditingCredential] = useState(null);
  const [formTestStatus, setFormTestStatus] = useState("");

  // Form states
  const [credentialForm, setCredentialForm] = useState({
    label: "",
    api_key: "",
  });

  const [credentialDialog, setCredentialDialog] = useState(false);

  useEffect(() => {
    loadCredentials();
  }, []);

  // Clear message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadCredentials = async (forceRefresh = false) => {
    try {
      setLoading(true);

      const CACHE_KEY = "webshare_credentials_cache";
      const cached = localStorage.getItem(CACHE_KEY);
      const CACHE_DURATION = 5 * 60 * 1000;

      if (!forceRefresh && cached && typeof forceRefresh !== "object") {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setCredentials(data || []);
          setLoading(false);
          return;
        }
      }

      const response = await webshareApi.getWebshareCredentials();

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response.data || [],
          timestamp: Date.now(),
        }),
      );

      setCredentials(response.data || []);
    } catch (error) {
      console.error("Failed to load credentials:", error);
      setError("Failed to load credentials: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveCredential = async (credential) => {
    try {
      setLoading(true);
      // Call backend to toggle status (backend ensures only one active)
      await webshareApi.toggleWebshareCredentialStatus(credential.id);

      setSuccess(`Toggled status for: ${credential.label || "Credential"}`);

      // Dispatch custom event for other tabs to react
      window.dispatchEvent(new Event("activeWebshareCredentialChanged"));

      await loadCredentials(true);
    } catch (error) {
      setError("Failed to toggle credential status: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testCredentialsFromForm = async () => {
    if (!credentialForm.api_key) {
      setFormTestStatus("❌ Please enter an API Key first");
      return;
    }

    try {
      setLoading(true);
      setFormTestStatus("Testing...");
      const response = await webshareApi.testWebshareCredentialKey(
        credentialForm.api_key,
      );

      if (response.data.success) {
        setFormTestStatus("✅ Authentication successful!");
      } else {
        setFormTestStatus(
          "❌ Authentication failed: " +
            (response.data.message || "Unknown error"),
        );
      }
    } catch (error) {
      setFormTestStatus("❌ Authentication failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testStoredCredential = async (credential) => {
    try {
      setLoading(true);
      const response = await webshareApi.testStoredWebshareCredential(
        credential.id,
      );

      if (response.data.success) {
        setSuccess(
          `✅ Test successful for ${credential.label || "Credential"}`,
        );
      } else {
        setError(
          `❌ Test failed for ${credential.label || "Credential"}: ${
            response.data.message
          }`,
        );
      }
    } catch (error) {
      setError(`❌ Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialSubmit = async () => {
    if (!credentialForm.api_key && !editingCredential) {
      setError("API Key is required");
      return;
    }
    if (!credentialForm.label) {
      setError("Label is required");
      return;
    }

    try {
      setLoading(true);

      if (editingCredential) {
        const payload = { ...credentialForm };
        if (!payload.api_key) {
          delete payload.api_key;
        }
        await webshareApi.updateWebshareCredential(
          editingCredential.id,
          payload,
        );
        setSuccess("Credential updated successfully!");
      } else {
        await webshareApi.createWebshareCredential(credentialForm);
        setSuccess("Credential created successfully!");
      }

      await loadCredentials(true);
      handleCloseDialog();
    } catch (error) {
      setError(
        `Failed to ${editingCredential ? "update" : "create"} credential: ` +
          error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditCredential = (credential) => {
    setEditingCredential(credential);
    setCredentialForm({
      label: credential.label || "",
      api_key: "",
    });
    setFormTestStatus("");
    setCredentialDialog(true);
  };

  const deleteCredential = async (credentialId) => {
    if (!window.confirm("Are you sure you want to delete this credential?")) {
      return;
    }

    try {
      setLoading(true);
      await webshareApi.deleteWebshareCredential(credentialId);
      await loadCredentials(true);
      setSuccess("Credential deleted successfully!");
    } catch (error) {
      setError("Failed to delete credential: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setCredentialDialog(false);
    setEditingCredential(null);
    setCredentialForm({
      label: "",
      api_key: "",
    });
    setFormTestStatus("");
    setError(null);
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card
            sx={{
              border: "3px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography variant="h6" sx={{ mr: 2 }}>
                  Webshare API Credentials
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadCredentials}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCredentialDialog(true)}
                  >
                    Add Credentials
                  </Button>
                </Box>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 3 }}
                  onClose={() => setSuccess(null)}
                >
                  {success}
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>How to Get Your Webshare Credentials:</strong>
                </Typography>
                <ol style={{ margin: 0, paddingLeft: "20px" }}>
                  <li>
                    Go to{" "}
                    <a
                      href="https://dashboard.webshare.io/userapi/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1976d2",
                        textDecoration: "underline",
                        fontWeight: "bold",
                      }}
                    >
                      Webshare Dashboard API Keys
                    </a>
                  </li>
                  <li>Create a new API Key</li>
                  <li>Copy the Key and Label</li>
                  <li>Enter them in the form below</li>
                </ol>
              </Alert>

              <List
                sx={{
                  width: "100%",
                  border: "2px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                {credentials.length === 0 ? (
                  <ListItem sx={{ px: 4, py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No credentials found. Click "Add Credentials" to create
                      one.
                    </Typography>
                  </ListItem>
                ) : (
                  credentials.map((credential) => (
                    <ListItem
                      key={credential.id}
                      divider
                      sx={{
                        px: 4,
                        py: 3,
                        borderBottom: "3px solid",
                        borderColor: "divider",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={8}>
                          <Typography variant="h6">
                            {credential.label || "Unnamed Credential"}
                          </Typography>
                          <Box
                            sx={{
                              mt: 0.5,
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            {credential.active && (
                              <Chip
                                label="ACTIVE"
                                size="small"
                                color="primary"
                                variant="filled"
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {/* Active Toggle Switch */}
                          <FormControlLabel
                            control={
                              <Switch
                                checked={credential.active}
                                onChange={() =>
                                  toggleActiveCredential(credential)
                                }
                                size="small"
                              />
                            }
                            label="Active"
                            labelPlacement="top"
                            sx={{ mr: 1 }}
                          />

                          <IconButton
                            title="Test Credential"
                            onClick={() => testStoredCredential(credential)}
                            color="info"
                            disabled={loading}
                          >
                            {loading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CheckIcon />
                            )}
                          </IconButton>
                          <IconButton
                            title="Edit Credential"
                            onClick={() => handleEditCredential(credential)}
                            color="default"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => deleteCredential(credential.id)}
                            title="Delete Credential"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={credentialDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: "2px solid",
            borderColor: "divider",
            ...cp.dialogHeader,
            color: "white",
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editingCredential ? <EditIcon /> : <AddIcon />}
            </Box>
            <Typography variant="h6" component="div" fontWeight={600}>
              {editingCredential
                ? "Edit Webshare Credential"
                : "Add Webshare Credential"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 4 }}>
          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                placeholder="e.g., Production Webshare"
                value={credentialForm.label}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    label: e.target.value,
                  }))
                }
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key"
                placeholder={
                  editingCredential
                    ? "Leave empty to keep unchanged"
                    : "Enter your Webshare API Token"
                }
                value={credentialForm.api_key}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    api_key: e.target.value,
                  }))
                }
                required={!editingCredential}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontFamily: "monospace",
                  },
                }}
              />
            </Grid>
            {formTestStatus && (
              <Grid item xs={12}>
                <Alert
                  severity={
                    formTestStatus.includes("✅")
                      ? "success"
                      : formTestStatus.includes("Testing")
                        ? "info"
                        : "error"
                  }
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                  }}
                  icon={
                    formTestStatus.includes("Testing") ? (
                      <CircularProgress size={20} />
                    ) : undefined
                  }
                >
                  {formTestStatus}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: "2px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={testCredentialsFromForm}
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
            disabled={loading || !credentialForm.api_key}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              minWidth: 140,
            }}
          >
            Test Key
          </Button>
          <Button
            onClick={handleCredentialSubmit}
            variant="contained"
            disabled={
              (!credentialForm.api_key && !editingCredential) ||
              !credentialForm.label ||
              loading
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              minWidth: 100,
              ...cp.dialogHeader,
              "&:hover": {
                background: "linear-gradient(135deg, #5568d3 0%, #6a4190 100%)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : editingCredential ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebshareCredentials;
